import {Link} from 'react-router';
import {useQuery} from '@tanstack/react-query';
import {apiClient} from '@common/http/query-client';
import type {BlockInstance} from '../appearance-data';
import {getAppearanceContent} from '../appearance-data';

interface Item {
  text?: string;
  link?: string;
}

/** Scrolling announcement / news ticker bar.
 *  Order: your custom announcements first, then (optionally) the latest blog
 *  posts from masertak.com. Pauses on hover. */
export function AnnouncementBlock({props}: {block: BlockInstance; props: Record<string, any>}) {
  // Custom announcements — from the block's own items, or the Announcements
  // manager (Content page), depending on `source`.
  const custom: Item[] = props.source === 'system'
    ? getAppearanceContent().announcements.map(a => ({text: a.text, link: a.link}))
    : (Array.isArray(props.items) ? props.items : []);

  // Optional: append the latest masertak.com WordPress posts (toggle).
  const wpEnabled = !!props.show_wordpress;
  const wpCount = Math.min(5, Math.max(1, Number(props.wp_count) || 3));
  const {data: wpPosts} = useQuery({
    queryKey: ['announcement-wp-posts', wpCount],
    queryFn: () =>
      apiClient.get('appearance/wp-posts', {params: {count: wpCount}}).then(r => r.data.posts as Item[]),
    enabled: wpEnabled,
    staleTime: 15 * 60 * 1000,
  });

  const items: Item[] = [
    ...custom.filter(i => i.text),
    ...(wpEnabled ? (wpPosts ?? []).filter(i => i.text) : []),
  ];
  if (!items.length) return null;

  // In RTL the bar should travel left-to-right (natural reading flow). Rather than
  // reversing the animation (which left visible gaps / made a single item vanish
  // mid-scroll under RTL layout), we force the moving strip to a fixed LTR
  // geometry and just swap the keyframes so it always stays seamlessly filled.
  const isRtl = typeof document !== 'undefined' && document.documentElement.dir === 'rtl';

  const duration = props.speed === 'slow' ? 40 : props.speed === 'fast' ? 12 : 22;
  // Always render the list twice: the strip is 2× wide and the animation shifts by
  // exactly one copy (50%), so the loop is seamless whether there's 1 item or 20.
  const rendered = [...items, ...items];

  const renderItem = (item: Item, i: number) => {
    // dir="auto" keeps Arabic/mixed text shaped correctly even though the strip
    // itself is laid out LTR for predictable scrolling geometry.
    const content = <span className="mx-20 text-sm" dir="auto">{item.text}</span>;
    return item.link ? (
      item.link.startsWith('http') ? (
        <a key={i} href={item.link} target="_blank" rel="noopener noreferrer" className="hover:underline">{content}</a>
      ) : (
        <Link key={i} to={item.link} className="hover:underline">{content}</Link>
      )
    ) : (
      <span key={i}>{content}</span>
    );
  };

  return (
    // The whole bar is forced to LTR layout so the scrolling strip (width:max-content)
    // is left-aligned and its transform geometry is identical to the LTR case — under
    // an RTL page it would otherwise right-align, leaving the gap/disappear. Per-item
    // dir="auto" keeps Arabic text shaped correctly inside each span.
    <div dir="ltr" className="group/marquee overflow-hidden"
      style={{backgroundColor: props.bg_color || '#111827', color: props.text_color || '#fff'}}>
      <div
        dir="ltr"
        className="flex w-max whitespace-nowrap py-8 group-hover/marquee:[animation-play-state:paused]"
        style={{
          animationName: isRtl ? 'ap-marquee-rtl' : 'ap-marquee',
          animationDuration: `${duration}s`,
          animationTimingFunction: 'linear',
          animationIterationCount: 'infinite',
          willChange: 'transform',
        }}
      >
        {rendered.map(renderItem)}
      </div>
      {/* Seamless loop of the duplicated list. LTR scrolls left (0→-50%); RTL
          scrolls right (-50%→0). Both stay inside the filled range, so there's
          never an empty gap. */}
      <style>{`@keyframes ap-marquee{from{transform:translateX(0)}to{transform:translateX(-50%)}}@keyframes ap-marquee-rtl{from{transform:translateX(-50%)}to{transform:translateX(0)}}`}</style>
    </div>
  );
}
